import { useCallback, useEffect, useRef } from 'react';
import { useLocalStorage } from 'usehooks-ts';

const UNLOCK_EVENTS = ['pointerdown', 'keydown'] as const;

export const useAudioNotification = () => {
  const [voiceAlerts] = useLocalStorage('voice_alerts_settings', {
    newConversation: true,
    handoff: true,
    newMessageToMe: true,
    newMessage: false,
  });

  const pendingRetryRef = useRef<(() => void) | null>(null);

  const clearPendingRetry = useCallback(() => {
    const retry = pendingRetryRef.current;
    if (!retry) return;
    pendingRetryRef.current = null;
    UNLOCK_EVENTS.forEach((event) => document.removeEventListener(event, retry));
  }, []);

  useEffect(() => clearPendingRetry, [clearPendingRetry]);

  // Autoplay-policy rejection is expected before the first user gesture: log info (not error,
  // so it never reaches RUM as an error) and replay the chime inside the next user gesture,
  // which browsers always allow. Any other playback failure stays a console.error so it
  // surfaces in RUM.
  const handlePlaybackError = useCallback(
    (audio: HTMLAudioElement, error: unknown) => {
      if (!(error instanceof DOMException) || error.name !== 'NotAllowedError') {
        console.error('Failed to play notification sound:', error);
        return;
      }
      console.info(`Notification sound blocked by autoplay policy: ${error.message}`);
      clearPendingRetry();
      const retry = () => {
        clearPendingRetry();
        audio.play().catch((retryError: unknown) => {
          console.error('Failed to play notification sound:', retryError);
        });
      };
      pendingRetryRef.current = retry;
      UNLOCK_EVENTS.forEach((event) => document.addEventListener(event, retry, { once: true }));
    },
    [clearPendingRetry],
  );

  const playNotification = useCallback(
    (alertType: keyof typeof voiceAlerts, source: string | undefined) => {
      if (
        (!voiceAlerts[alertType] && source !== 'toggle') ||
        (voiceAlerts[alertType] && source === 'toggle')
      )
        return;

      const audio = new Audio(`/static/audio/alerts/${alertType}.mp3`);
      audio.volume = 0.5;
      audio.preload = 'auto';

      audio.play().catch((error: unknown) => {
        handlePlaybackError(audio, error);
      });
    },
    [voiceAlerts, handlePlaybackError],
  );

  return { playNotification };
};
